import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import type { BudgetLine } from '@/lib/api';
import { categoriesOfGroup } from '@/lib/constants';
import { formatCurrency, formatPercentGlance } from '@/lib/format';
import { lineState } from '@/lib/utils/budget';
import {
  BUDGET_COLUMN_LABELS,
  BUDGET_LINE_NOTES,
  BUDGET_SEE_CATEGORIES,
  budgetGoalMeta,
  budgetLimitOf,
  translateBudgetType,
} from '@/lib/translations';
import { BudgetBar } from './BudgetBar';

export interface BudgetLineRowProps {
  line: BudgetLine;
  /** The month the row reads ("2026-09"), carried into Categorias so its way into Transações reads it too. */
  period?: string;
}

const linkFocus =
  'rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

// The one place semantic color enters a row: a 6px expense dot where a bucket has spent past its limit or
// has no limit at all (Semantic Anchor Rule — a dot, never a red row or a red figure).
const ExpenseAnchor = () => (
  <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-expense align-middle" aria-hidden="true" />
);

const Note = ({ children }: { children: React.ReactNode }) => (
  <span className="ml-2 rounded-sm border border-border px-1.5 py-px text-xs text-muted-foreground">{children}</span>
);

// One bucket of the month. How it reads is decided by lineState (TRD §9), never by the raw `used` string:
// with no limit the API reports 0.00 by convention, and showing that as a measurement is exactly how a
// bucket nobody allocated money to ends up claiming to be fully used.
export const BudgetLineRow: React.FC<BudgetLineRowProps> = ({ line, period }) => {
  const state = lineState(line);
  const spent = parseFloat(line.spent);
  const mustSpend = parseFloat(line.must_spend);

  // The figure at the right of the first line — what `used` reads as for this state.
  let used: React.ReactNode;
  switch (state.kind) {
    case 'within':
      used = formatPercentGlance(state.usedPercent);
      break;
    case 'over':
      // Not capped: the bar stops at 100, the number keeps going (116,67%) — and says so in a word, since a dot
      // alone is easy to miss at month's end.
      used = (
        <>
          <ExpenseAnchor />
          {formatPercentGlance(state.usedPercent)}
          <span className="ml-1.5 font-normal text-muted-foreground" aria-hidden="true">
            {BUDGET_LINE_NOTES.overShort}
          </span>
          <span className="sr-only">, {BUDGET_LINE_NOTES.over}</span>
        </>
      );
      break;
    case 'unbounded':
      used = (
        <>
          <ExpenseAnchor />
          {BUDGET_LINE_NOTES.unbounded}
        </>
      );
      break;
    default:
      // refunded, idle, negative-allowance: there is no percentage to give.
      used = (
        <>
          <span aria-hidden="true">—</span>
          <span className="sr-only">{BUDGET_LINE_NOTES.noPercentage}</span>
        </>
      );
  }

  const barValue = state.kind === 'within' || state.kind === 'over' ? state.usedPercent : state.kind === 'refunded' ? 0 : null;

  // idle has nothing to say beyond its dash; every other state shows what was spent.
  const showMoney = state.kind !== 'idle';
  // A limit is only worth stating when it is a real one; a refund in a bucket with no allowance yet has none.
  const showLimit = state.kind === 'within' || state.kind === 'over' || state.kind === 'negative-allowance';
  const note = state.kind === 'negative-allowance' ? BUDGET_LINE_NOTES.deduction : null;
  // Past the limit, or spending with none: the row says where to look, not only that something is off.
  const pastLimit = state.kind === 'over' || state.kind === 'unbounded';
  const groupName = translateBudgetType(line.type);
  const groupHref = categoriesOfGroup(line.type, period);

  return (
    <li className="py-4">
      <div className="flex items-baseline justify-between gap-x-4">
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
          {/* The name leads to the categories that fed the group (Categorias, narrowed to it, same month). No
              aria-label: it would rename the heading, and the group's name is what a heading list should read. */}
          <h3 className="text-base font-semibold text-foreground">
            <Link
              to={groupHref}
              className={`underline-offset-4 decoration-foreground/30 hover:underline ${linkFocus}`}
            >
              {groupName}
            </Link>
          </h3>
          <span className="text-xs tabular-nums text-muted-foreground">{budgetGoalMeta(line.percentage)}</span>
        </div>
        <p className="shrink-0 text-sm font-semibold tabular-nums text-foreground">
          <span className="sr-only">{BUDGET_COLUMN_LABELS.used}: </span>
          {used}
        </p>
      </div>

      {barValue !== null && <BudgetBar value={barValue} over={state.kind === 'over'} className="mt-2.5" />}

      {showMoney && (
        <div className="mt-2 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 text-sm">
          <p className="tabular-nums">
            <span className="sr-only">{BUDGET_COLUMN_LABELS.spent}: </span>
            {state.kind === 'refunded' ? (
              // What came back, as a positive amount: "R$ 50,00 em reembolsos", never "−R$ 50,00 de R$ 1.005,84".
              <>
                <span className="font-semibold text-foreground">{formatCurrency(Math.abs(spent))}</span>
                <span className="text-muted-foreground"> {BUDGET_LINE_NOTES.refundedAmount}</span>
              </>
            ) : (
              <>
                <span className="font-semibold text-foreground">{formatCurrency(spent)}</span>
                {showLimit && <span className="text-muted-foreground"> {budgetLimitOf(mustSpend)}</span>}
              </>
            )}
            {note && <Note>{note}</Note>}
          </p>
          {pastLimit && (
            <Link
              to={groupHref}
              className={`inline-flex min-h-11 items-center gap-1 text-xs font-medium text-foreground underline-offset-4 hover:underline sm:min-h-0 ${linkFocus}`}
            >
              {BUDGET_SEE_CATEGORIES}
              <span className="sr-only"> — {groupName}</span>
              <ArrowRight className="h-3 w-3" aria-hidden="true" />
            </Link>
          )}
        </div>
      )}
    </li>
  );
};
